import { useEffect, useRef, useCallback } from 'react'
import { useAuthStore } from '@/store/auth-store'
import { useNotificationStore } from '@/store/notification-store'
import { notificationsService } from '@/services/notifications-service'

const POLL_INTERVAL = 15_000 // 15 seconds

export function useNotificationPoller() {
  const { isAuthenticated } = useAuthStore()
  const { setNotifications, setUnreadCount } = useNotificationStore()
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollingRef = useRef(false)

  const poll = useCallback(async () => {
    if (pollingRef.current) return
    pollingRef.current = true
    try {
      const [listResult, count] = await Promise.all([
        notificationsService.list(0, 30),
        notificationsService.getUnreadCount(),
      ])
      setNotifications(listResult.notifications)
      setUnreadCount(count)
    } catch {
      // Silently fail — next poll will retry
    } finally {
      pollingRef.current = false
    }
  }, [setNotifications, setUnreadCount])

  useEffect(() => {
    if (!isAuthenticated) return

    // Fetch immediately on mount
    poll()

    // Poll on interval
    intervalRef.current = setInterval(poll, POLL_INTERVAL)

    // Re-poll immediately when the user returns to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') poll()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAuthenticated, poll])

  return { poll }
}
